import React, { useState, useEffect } from 'react';
import '../styles/analytics-responsive.css';
import { cleanCategoryName, formatYAxisNumber } from '../utils';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';

// ─── STAT CARD ───────────────────────────────────────────────────────────────
const StatCard = ({ label, value, sub, change, changeColor }) => {
  const isPositive = changeColor ? changeColor === '#27ae60' : parseFloat(change) >= 0;
  const resolvedColor = changeColor || (isPositive ? '#27ae60' : '#e74c3c');
  return (
    <div className="analytics-stat-card" style={{
      background: '#fff',
      border: '1px solid #e8edf2',
      borderRadius: 8,
      padding: '14px 16px',
      flex: 1,
      minWidth: 0,
    }}>
      <div className="stat-label" style={{ fontSize: 14, color: '#888', fontWeight: 700, textTransform: 'none', letterSpacing: 0.3, whiteSpace: 'nowrap', marginBottom: 4 }}>{label}</div>
      <div className="stat-value" style={{ fontSize: 26, fontWeight: 700, color: '#1a1a2e', lineHeight: 1.1, marginBottom: 2 }}>{value}</div>
      <div style={{ fontSize: 12, color: '#aaa', fontWeight: 500, marginBottom: 4 }}>{sub}</div>
      {change !== undefined && <div style={{ fontSize: 12, color: resolvedColor, fontWeight: 700 }}>{isPositive ? '▲' : '▼'} {change}</div>}
    </div>
  );
};

// ─── PROGRESS BAR ────────────────────────────────────────────────────────────
const ProgressBarRow = ({ label, stats, percent }) => {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 13, fontWeight: 600, color: '#333' }}>
        <span>{label}</span>
        <span>{stats}</span>
      </div>
      <div style={{ width: '100%', height: 8, background: '#e0e6ed', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ width: `${percent}%`, height: '100%', background: '#00897b', borderRadius: 4 }}></div>
      </div>
    </div>
  );
};

// ─── REUSABLE NO-DATA MESSAGE ────────────────────────────────────────────────
const NoDataMessage = ({ message = "No data available" }) => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    width: '100%',
    color: '#aaa',
    fontSize: 14,
    fontWeight: 500,
    minHeight: 160,
  }}>
    {message}
  </div>
);

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────
const OperationsAnalytics = () => {
  const [activeFilter, setActiveFilter] = useState('1mo');
  const [kpiData, setKpiData] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [performanceData, setPerformanceData] = useState(null);
  const [loading, setLoading] = useState(true);

  const filters = ['1mo', '3mo', '6mo', '1yr'];

  useEffect(() => {
    const fetchKpis = async () => {
      setLoading(true);
      try {
        const API = process.env.REACT_APP_BASE_API_URL || 'http://localhost:4000';
        const res = await fetch(`${API}/api/operations-analytics/kpis?filter=${activeFilter}`);
        const data = await res.json();

        if (data.success) {
          setKpiData(data.kpiData);
          if (data.performanceData) setPerformanceData(data.performanceData);
          if (data.chartData) {
            let parsedData = data.chartData;
            if (activeFilter === '1yr' || activeFilter === '6mo' || activeFilter === '3mo') {
              const monthsCount = activeFilter === '1yr' ? 12 : (activeFilter === '6mo' ? 6 : 3);
              const now = new Date();
              const padded = [];
              for (let i = monthsCount - 1; i >= 0; i--) {
                const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
                const label = d.toLocaleString('en-US', { month: 'short' });
                const existing = parsedData.find(r => r.month === label);
                padded.push(existing || { month: label, rate: 0 });
              }
              parsedData = padded;
            } else if (activeFilter === '1mo') {
              const now = new Date();
              const padded = [];
              const startDate = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
              const daysDiff = Math.floor((now - startDate) / (1000 * 60 * 60 * 24));
              for (let i = daysDiff; i >= 0; i--) {
                const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - i);
                const label = d.getDate() + ' ' + d.toLocaleString('en-US', { month: 'short' });
                const existing = parsedData.find(r => r.month === label);
                padded.push(existing || { month: label, rate: 0 });
              }
              parsedData = padded;
            }
            setChartData(parsedData);
          } else {
            setChartData([]);
          }
        }
      } catch (error) {
        console.error('Error fetching ops kpis:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchKpis();
  }, [activeFilter]);

  return (
    <div className="analytics-root" style={{
      minWidth: 1024,
      fontFamily: "'Segoe UI', sans-serif",
      color: '#1a1a2e',
      padding: '24px 32px',
      background: '#fff',
      minHeight: '100vh'
    }}>
      <style>{`
        .recharts-wrapper svg,
        .recharts-wrapper svg * {
          outline: none !important;
        }
      `}</style>

      <div style={{ maxWidth: 1400, margin: '0 auto', padding: '16px 0' }}>

        {/* ── PAGE TITLE + FILTER ── */}
        <div className="analytics-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div>
            <h4 style={{ fontWeight: 700, fontSize: 20, marginBottom: 2, color: '#1a1a2e' }}>Operations Analytics</h4>
            <p style={{ fontSize: 12, color: '#888', margin: 0 }}>Tender operations and revenue tracking</p>
          </div>
          <div className="analytics-filters" style={{ display: 'flex', gap: 8 }}>
            {filters.map(f => (
              <button
                key={f}
                onClick={() => {
                  if (activeFilter !== f) {
                    setLoading(true);
                    setActiveFilter(f);
                  }
                }}
                style={{
                  padding: '6px 18px',
                  border: activeFilter === f ? '2px solid #00897b' : '1.5px solid #e0e0e0',
                  borderRadius: 50,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  background: activeFilter === f ? '#e0f5f3' : '#fff',
                  color: activeFilter === f ? '#00897b' : '#888',
                  transition: 'all 0.2s',
                }}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        {/* TOP ROW STATS (6 cards) */}
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>Loading Data...</div>
        ) : !kpiData ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', padding: '60px 0', color: '#aaa', fontSize: 14, fontWeight: 500 }}>
            No data available
          </div>
        ) : (
          <>
            <div className="analytics-stats-row" style={{ display: 'flex', gap: 16, marginBottom: 32 }}>
              <StatCard label="TOTAL BIDS" value={kpiData.totalBids?.value || '0'} sub="vs previous" change={kpiData.totalBids?.change} />
              <StatCard label="TENDERS WON" value={kpiData.tendersWon?.value || '0'} sub="vs previous" change={kpiData.tendersWon?.change} />
              <StatCard label="TENDERS LOST" value={kpiData.tendersLost?.value || '0'} sub="vs previous" change={kpiData.tendersLost?.change} />
              <StatCard label="WIN RATE" value={kpiData.winRate?.value || '0%'} sub="vs previous" change={kpiData.winRate?.change} />
              <StatCard label="TOTAL BID VALUE" value={kpiData.totalBidValue?.value || '₹0'} sub="vs previous" change={kpiData.totalBidValue?.change} />
              <StatCard label="REVENUE" value={kpiData.revenue?.value || '₹0'} sub="via subscriptions" change={kpiData.revenue?.change} />
            </div>

            <hr style={{ border: 'none', borderTop: '1px solid #e8edf2', margin: '32px 0' }} />

            {/* CHARTS SECTION */}
            <div className="analytics-chart-row" style={{ display: 'flex', gap: 24 }}>

              {/* Line Chart */}
              <div className="analytics-chart-panel" style={{ flex: '1.5', background: '#fff', border: '1px solid #e8edf2', borderRadius: 8, padding: '20px 24px', display: 'flex', flexDirection: 'column' }}>
                <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 2, color: '#1a1a2e' }}>
                  {activeFilter === '1yr' ? 'Yearly Win Rate Trend' : 'Monthly Win Rate Trend'}
                </div>
                <div style={{ fontSize: 12, color: '#888', fontWeight: 700, textTransform: 'none', letterSpacing: 0.3, marginBottom: 12 }}>Win rate trend over selected period</div>
                <div style={{ flex: 1, minHeight: 260 }}>
                  {chartData.length === 0 ? (
                    <NoDataMessage />
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                        <defs>
                          <linearGradient id="colorRate" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#00897b" stopOpacity={0.15} />
                            <stop offset="95%" stopColor="#00897b" stopOpacity={0.0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                        <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#888' }} dy={10} interval={activeFilter === '1mo' ? 2 : 'preserveStartEnd'} />
                        <YAxis tickFormatter={formatYAxisNumber} axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#888' }} />
                        <Tooltip
                          contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                          itemStyle={{ color: '#00897b', fontWeight: 600 }}
                          labelStyle={{ color: '#888', marginBottom: 4 }}
                          formatter={(value) => [`Win Rate: ${value}%`, '']}
                        />
                        <Area
                          type="monotone"
                          dataKey="rate"
                          stroke="#00897b"
                          strokeWidth={3}
                          fillOpacity={1}
                          fill="url(#colorRate)"
                          activeDot={{ r: 6, fill: '#00897b', stroke: '#fff', strokeWidth: 2 }}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>

              {/* Progress Bars */}
              <div className="analytics-chart-panel" style={{ flex: '1', background: '#fff', border: '1px solid #e8edf2', borderRadius: 8, padding: '24px' }}>
                <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 2, color: '#1a1a2e' }}>
                  Performance vs Platform Average
                </div>
                <div style={{ fontSize: 12, color: '#888', fontWeight: 700, textTransform: 'none', letterSpacing: 0.3, marginBottom: 20 }}>Current period vs all-time benchmark</div>
                {performanceData ? (
                  <>
                    <ProgressBarRow
                      label="Win Rate"
                      stats={performanceData.winRate.stats}
                      percent={Math.min(100, performanceData.winRate.user)}
                    />
                    <br />
                    <ProgressBarRow
                      label="Avg Bid Value"
                      stats={performanceData.avgBidValue.stats}
                      percent={Math.min(100, performanceData.avgBidValue.user)}
                    />
                    <br />
                    <ProgressBarRow
                      label="L1 Rate"
                      stats={performanceData.l1Rate.stats}
                      percent={Math.min(100, performanceData.l1Rate.user)}
                    />
                    <br />
                    <ProgressBarRow
                      label="Response Rate"
                      stats={performanceData.responseRate.stats}
                      percent={Math.min(100, performanceData.responseRate.user)}
                    />
                  </>
                ) : (
                  <NoDataMessage />
                )}
              </div>

            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default OperationsAnalytics;